<script setup lang="ts">
/*
  * - Core component for loading and displaying VRM model
  * - Load model, get some geometry data for initialisation
  * - Shader injection and rendering setting
  * - Load & initialise animation
*/

import type { VRM } from '@pixiv/three-vrm'
import type {
  AnimationAction,
  AnimationClip,
  Group,
  Material,
  Object3D,
  PerspectiveCamera,
  ShaderMaterial,
  SphericalHarmonics3,

  Texture,
  WebGLRenderer,
} from 'three'

import type {
  VrmDisposeHookContext,
  VrmFrameHookContext,
  VrmHook,
  VrmLoadHookContext,
  VrmMaterialHookContext,
} from '../../composables/vrm/hooks'
import type { SceneBootstrap, TrackingMode, Vec3 } from '../../stores/model-store'
import type { VrmLifecycleReason } from '../../trace'
import type { IdlePlaybackMode, ManagedVrmInstance } from './vrm-instance-cache'

import { VRMUtils } from '@pixiv/three-vrm'
import { useLoop, useTresContext } from '@tresjs/core'
import { until } from '@vueuse/core'
import {
  AnimationMixer,
  Box3,
  MathUtils,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Raycaster,

  SRGBColorSpace,
  Vector3,
} from 'three'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,

  shallowRef,

  toRefs,
  watch,

} from 'vue'

import * as THREE from 'three'

import { useVRMEyeFocusFor } from '../../composables/eye-tracking'
import {
  createIblProbeController,
  injectDiffuseIBL,
  normalizeEnvMode,
  updateNprShaderSetting,
} from '../../composables/shader/ibl'
// From stage-ui-three package
import {
  clipFromVRMAnimation,
  loadVRMAnimation,
  reAnchorRootPositionTrack,
  useBlink,
  useIdleEyeSaccades,
} from '../../composables/vrm/animation'
import { loadVrm } from '../../composables/vrm/core'
import { useVRMEmote } from '../../composables/vrm/expression'
import { resolveInternalVrmHooks } from '../../composables/vrm/internal-hooks'
import { useVRMLipSync } from '../../composables/vrm/lip-sync'
import {
  createThreeRendererMemorySnapshot,
  createVrmSceneSummarySnapshot,
  getStageThreeRuntimeTraceContext,
  isStageThreeRuntimeTraceEnabled,
  stageThreeTraceVrmDisposeEndEvent,
  stageThreeTraceVrmDisposeStartEvent,
  stageThreeTraceVrmLoadEndEvent,
  stageThreeTraceVrmLoadErrorEvent,
  stageThreeTraceVrmLoadStartEvent,
  stageThreeTraceVrmUpdateFrameEvent,
} from '../../trace'
import { clearManagedVrmInstance, getVrmDataCache, setVrmDataCache, stashManagedVrmInstance, takeManagedVrmInstance } from './vrm-instance-cache'

/*
  * Props:
  * - modelSrc: model src string to load model asset
  * - idleAnimation: animation src for model
  * - loadAnimations: TBC
  * - paused: if the animation is paused
  * - nprIrrSH: Spherical Harmonics computed from the sky box, used for IBL
  *
  * - modelOffset: The placing offset of model (x, y, z)
  * - modelRotationY: The rotation of the model (y-axis)
*/
const props = withDefaults(defineProps<{
  currentAudioSource?: AudioBufferSourceNode
  cursorPosition?: { x: number, y: number }
  lastCommittedModelSrc?: string
  modelSrc?: string
  /** 初始动画阶段：greet（欢迎）或 idle（待机） */
  animationPhase?: 'greet' | 'idle'
  /** greet动画URL（当animationPhase为'greet'时使用） */
  greetAnimation?: string
  idleAnimation?: string
  // Ordered list of .vrma URLs to cycle through as the idle animation.
  // When provided and non-empty, it takes precedence over `idleAnimation`
  // and the model cross-fades between entries every `playlistIntervalMs`.
  // 改为随机选择idle动画，而不是按固定顺序
  idleAnimationPlaylist?: string[]
  // loadAnimations?: string[]
  paused?: boolean

  envSelect: string
  skyBoxIntensity: number
  nprIrrSH?: SphericalHarmonics3 | null

  modelOffset: Vec3
  modelRotationY: number
  trackingMode: TrackingMode
  eyeHeight: number
  screenBoundingBox: () => { top: number, left: number, width: number, height: number }
  cameraPosition: Vec3

  camera: PerspectiveCamera
}>(), {
  animationPhase: 'idle',
  paused: false,
})
/*
  * Emits:
  * - model-core-loading-progress
  * - model-core-error
  * - model-core-ready
  * - animation-complete: 动画播放完成（greet完成后触发）
  *
*/
const emit = defineEmits<{
  (e: 'loadingProgress', value: number): void
  (e: 'loadStart', value: 'initial-load' | 'model-reload' | 'model-switch'): void
  (e: 'sceneBootstrap', value: SceneBootstrap): void

  (e: 'error', value: unknown): void
  (e: 'loaded', value: string): void
  (e: 'animationComplete', phase: 'greet' | 'idle'): void
}>()

const {
  currentAudioSource,
  lastCommittedModelSrc,
  modelSrc,
  animationPhase,
  greetAnimation,
  idleAnimation,
  idleAnimationPlaylist,
  // loadAnimations, // TBC
  paused,

  envSelect,
  skyBoxIntensity,
  nprIrrSH,

  modelOffset,
  modelRotationY,
  eyeHeight,

  camera,
} = toRefs(props)

// Model and scene ref
const { renderer, scene } = useTresContext()
const vrm = shallowRef<VRM>()
const vrmGroup = shallowRef<Group>()
const modelLoaded = ref<boolean>(false)
let loadSequence = 0
// for eye tracking modes
const raycaster = new Raycaster()

// Animation related ref
const vrmAnimationMixer = ref<AnimationMixer>()
const { onBeforeRender, stop, start } = useLoop()

const vrmHooks: readonly VrmHook[] = resolveInternalVrmHooks()
type VrmFrameRuntimeHook = (vrm: VRM, delta: number) => void
const vrmFrameRuntimeHook = shallowRef<VrmFrameRuntimeHook>()
let disposeBeforeRenderLoop: (() => void | undefined) | undefined

// --- Animation state machine ---
// 支持greet → idle的动画状态机，以及idle动画的随机选择
const PLAYLIST_INTERVAL_MIN_MS = 18000 // idle动画随机切换最短间隔
const PLAYLIST_INTERVAL_MAX_MS = 32000 // idle动画随机切换最长间隔
const PLAYLIST_CROSSFADE_DURATION = 1.2 // seconds，动画过渡时长
let playlistTimerId: ReturnType<typeof setInterval> | undefined
let playlistActions: AnimationAction[] = []
let playlistIndex = 0 // 当前播放的动画索引
let greetAction: AnimationAction | undefined
let idleActionsLoaded: AnimationAction[] = [] // 预加载的idle动画actions
let greetAnimationClip: AnimationClip | undefined // 用于判断是否是greet动画
let currentIdlePlaybackMode: IdlePlaybackMode = 'cycle'
let holdPoseAction: AnimationAction | undefined
let holdPoseAnimationClip: AnimationClip | undefined
const HOLD_POSE_END_EPSILON = 1 / 60
const MAX_SAFE_SPRING_BONE_DELTA_SECONDS = 0.05
const IDLE_NOISE_INTENSITY_SCALE = 1.0
const IDLE_NOISE_HIPS_SWAY_RAD = MathUtils.degToRad(1.2 * IDLE_NOISE_INTENSITY_SCALE)
const IDLE_NOISE_HIPS_TWIST_RAD = MathUtils.degToRad(0.8 * IDLE_NOISE_INTENSITY_SCALE)
const IDLE_NOISE_HEAD_PITCH_RAD = MathUtils.degToRad(0.7 * IDLE_NOISE_INTENSITY_SCALE)
const IDLE_NOISE_HEAD_YAW_RAD = MathUtils.degToRad(0.9 * IDLE_NOISE_INTENSITY_SCALE)
const IDLE_NOISE_HEAD_ROLL_RAD = MathUtils.degToRad(1.1 * IDLE_NOISE_INTENSITY_SCALE)
const IDLE_NOISE_NECK_FOLLOW_RATIO = 0.35
const IDLE_NOISE_ZERO_GRAVITY_EPSILON = 1e-4

interface IdleNoiseSpringJointState {
  joint: {
    settings: {
      dragForce: number
      gravityDir: Vector3
      gravityPower: number
      stiffness: number
    }
  }
  originalDragForce: number
  originalGravityDir: Vector3
  originalGravityPower: number
  originalStiffness: number
}

interface IdleNoiseMotionState {
  elapsedSeconds: number
  springJoints: IdleNoiseSpringJointState[]
}

interface StaticPoseBlendState {
  durationSeconds: number
  elapsedSeconds: number
  fromAction: AnimationAction
  toAction: AnimationAction
}

const idleNoiseMotionStateByVrm = new WeakMap<VRM, IdleNoiseMotionState>()
const idleNoiseWindVector = new Vector3()
const idleNoiseGravityVector = new Vector3()

// 用于loadModel函数中的临时变量（传递给commitManagedVrmInstance）
let nextAnimationPhase: 'greet' | 'idle' = 'idle'
let nextIdlePlaybackMode: IdlePlaybackMode = 'cycle'
let staticPoseBlendState: StaticPoseBlendState | undefined

function clearHoldPoseListener() {
  const mixer = vrmAnimationMixer.value ?? holdPoseAction?.getMixer()
  if (mixer && holdPoseAction) {
    mixer.removeEventListener('finished', onHoldPoseAnimationFinished)
  }

  holdPoseAction = undefined
  holdPoseAnimationClip = undefined
}

function getHoldPoseClampTime(action: AnimationAction) {
  return Math.max(action.getClip().duration - HOLD_POSE_END_EPSILON, 0)
}

function isStaticHoldPoseAction(action: AnimationAction) {
  const clip = action.getClip()
  if (clip.duration <= HOLD_POSE_END_EPSILON) {
    return true
  }

  return clip.tracks.every(track => track.times.length <= 1)
}

function freezeHoldPoseAtLastFrame(action: AnimationAction, reason: string, effectiveWeight = 1) {
  const mixer = vrmAnimationMixer.value ?? action.getMixer()
  const clampTime = getHoldPoseClampTime(action)

  action.play()
  action.enabled = true
  action.setEffectiveWeight(effectiveWeight)
  action.setEffectiveTimeScale(1)
  action.time = clampTime
  action.paused = true
  mixer?.update(0)
}

function clearStaticPoseBlend(options: { stopSourceAction?: boolean } = {}) {
  const activeBlend = staticPoseBlendState
  staticPoseBlendState = undefined
  if (!activeBlend) {
    return
  }

  activeBlend.toAction.enabled = true
  activeBlend.toAction.setEffectiveWeight(1)
  if (options.stopSourceAction) {
    activeBlend.fromAction.stop()
  }
}

function startStaticPoseBlend(fromAction: AnimationAction, toAction: AnimationAction, reason: string) {
  clearStaticPoseBlend({ stopSourceAction: true })

  if (fromAction === toAction) {
    freezeHoldPoseAtLastFrame(toAction, `${reason}-same-action`)
    return
  }

  if (isStaticHoldPoseAction(fromAction)) {
    freezeHoldPoseAtLastFrame(fromAction, `${reason}-source`)
  }
  else {
    fromAction.enabled = true
    fromAction.setEffectiveWeight(1)
    fromAction.setEffectiveTimeScale(1)
  }

  freezeHoldPoseAtLastFrame(toAction, reason, 0)
  staticPoseBlendState = {
    durationSeconds: PLAYLIST_CROSSFADE_DURATION,
    elapsedSeconds: 0,
    fromAction,
    toAction,
  }
}

function updateStaticPoseBlend(delta: number) {
  const activeBlend = staticPoseBlendState
  if (!activeBlend) {
    return
  }

  const safeDelta = Math.min(Math.max(delta, 0), PLAYLIST_CROSSFADE_DURATION)
  activeBlend.elapsedSeconds = Math.min(activeBlend.elapsedSeconds + safeDelta, activeBlend.durationSeconds)

  const rawProgress = activeBlend.durationSeconds <= 0
    ? 1
    : activeBlend.elapsedSeconds / activeBlend.durationSeconds
  const progress = MathUtils.clamp(rawProgress, 0, 1)
  const easedProgress = progress * progress * (3 - 2 * progress)

  activeBlend.fromAction.enabled = true
  activeBlend.toAction.enabled = true
  activeBlend.fromAction.setEffectiveWeight(1 - easedProgress)
  activeBlend.toAction.setEffectiveWeight(easedProgress)

  if (progress >= 1) {
    activeBlend.fromAction.stop()
    freezeHoldPoseAtLastFrame(activeBlend.toAction, 'static-pose-blend-complete')
    staticPoseBlendState = undefined
  }
}

function getRandomPlaylistDelayMs() {
  return Math.round(MathUtils.randFloat(PLAYLIST_INTERVAL_MIN_MS, PLAYLIST_INTERVAL_MAX_MS))
}

function registerHoldPoseListener(action: AnimationAction) {
  const mixer = vrmAnimationMixer.value ?? action.getMixer()
  clearHoldPoseListener()
  holdPoseAction = action
  holdPoseAnimationClip = action.getClip()
  mixer?.addEventListener('finished', onHoldPoseAnimationFinished)
}

function onHoldPoseAnimationFinished(event: { action: AnimationAction }) {
  const eventClip = event.action.getClip()
  if (!holdPoseAction || !holdPoseAnimationClip || event.action !== holdPoseAction || eventClip.uuid !== holdPoseAnimationClip.uuid) {
    return
  }

  freezeHoldPoseAtLastFrame(event.action, 'finished-event')
}

function shouldReuseHoldPoseAction(action: AnimationAction) {
  return action.paused || action.isRunning() || action.time > 0
}

function playHoldPoseAction(action: AnimationAction, options: { reuseExisting?: boolean } = {}) {
  clearStaticPoseBlend({ stopSourceAction: true })
  registerHoldPoseListener(action)

  if (options.reuseExisting && shouldReuseHoldPoseAction(action)) {
    if (action.paused || action.time >= getHoldPoseClampTime(action)) {
      freezeHoldPoseAtLastFrame(action, 'reuse-existing')
    }

    return
  }

  if (isStaticHoldPoseAction(action)) {
    freezeHoldPoseAtLastFrame(action, 'static-pose')
    return
  }

  action.reset()
  action.setEffectiveWeight(1)
  action.setEffectiveTimeScale(1)
  action.setLoop(THREE.LoopOnce, 1)
  action.clampWhenFinished = true
  action.play()
}

/**
 * 播放greet动画，完成后自动进入idle
 */
function playGreetAnimation(action: AnimationAction, idleActions: AnimationAction[], idlePlaybackMode: IdlePlaybackMode) {
  stopPlaylistCycle()
  greetAction = action
  greetAnimationClip = action.getClip() // 保存clip用于比较
  idleActionsLoaded = idleActions // 保存idle动画供后续使用
  currentIdlePlaybackMode = idlePlaybackMode

  // 设置动画只播放一次（不循环）
  action.setLoop(THREE.LoopOnce, 1)
  action.clampWhenFinished = true // 动画完成后保持最后一帧
  action.reset()
  action.setEffectiveWeight(1)
  action.setEffectiveTimeScale(1)
  action.play()

  // 监听动画完成事件
  const mixer = vrmAnimationMixer.value
  if (mixer) {
    mixer.addEventListener('finished', onGreetAnimationFinished)
  }
}

/**
 * greet动画完成回调 - 自动切换到idle动画
 */
function onGreetAnimationFinished(event: { action: AnimationAction }) {
  const eventClip = event.action.getClip()

  if (!greetAnimationClip || !eventClip || eventClip.uuid !== greetAnimationClip.uuid) {
    return
  }

  const mixer = vrmAnimationMixer.value
  if (mixer) {
    mixer.removeEventListener('finished', onGreetAnimationFinished)
  }

  if (idleActionsLoaded.length > 0) {
    const randomIndex = Math.floor(Math.random() * idleActionsLoaded.length)
    const idleAction = idleActionsLoaded[randomIndex]

    if (currentIdlePlaybackMode === 'hold') {
      startStaticPoseBlend(greetAction!, idleAction, 'greet-to-hold-pose')
      playlistActions = [idleAction]
      playlistIndex = 0
    }
    else {
      idleAction.reset()
      idleAction.setEffectiveWeight(1)
      idleAction.setEffectiveTimeScale(1)
      idleAction.setLoop(THREE.LoopRepeat, Infinity)
      idleAction.clampWhenFinished = false
      idleAction.play()

      playlistActions = idleActionsLoaded
      playlistIndex = randomIndex
      const scheduleNextPlaylistSwitch = () => {
        const nextDelayMs = getRandomPlaylistDelayMs()
        playlistTimerId = setTimeout(() => {
          scheduleNextPlaylistSwitch()

          const currentAction = playlistActions[playlistIndex]
          let nextIndex = Math.floor(Math.random() * playlistActions.length)
          if (nextIndex === playlistIndex && playlistActions.length > 1) {
            nextIndex = (nextIndex + 1) % playlistActions.length
          }
          playlistIndex = nextIndex

          const nextAction = playlistActions[playlistIndex]
          nextAction.reset()
          nextAction.setEffectiveWeight(0)
          nextAction.setEffectiveTimeScale(1)
          nextAction.setLoop(THREE.LoopRepeat, Infinity)
          nextAction.clampWhenFinished = false
          nextAction.play()

          currentAction.crossFadeTo(nextAction, PLAYLIST_CROSSFADE_DURATION, false)
        }, nextDelayMs)
      }
      scheduleNextPlaylistSwitch()
    }
  }

  greetAction = undefined
  greetAnimationClip = undefined

  emit('animationComplete', 'greet')
}

/**
 * 开始idle动画循环（随机选择idle动画）
 * @param actions idle动画actions列表
 * @param startIndex 初始播放的动画索引（可选，默认随机选择）
 */
function startPlaylistCycle(actions: AnimationAction[], startIndex?: number) {
  stopPlaylistCycle()
  if (actions.length === 0) {
    return
  }
  currentIdlePlaybackMode = 'cycle'

  // 如果没有指定起始索引，随机选择
  const initialIndex = startIndex ?? Math.floor(Math.random() * actions.length)

  // Reset to a clean state
  for (const action of actions) {
    action.stop()
  }

  // 如果是从greet切换过来的，当前动画已经在播放了，不需要重新设置
  if (startIndex === undefined) {
    const initialAction = actions[initialIndex]

    if (isStaticHoldPoseAction(initialAction)) {
      freezeHoldPoseAtLastFrame(initialAction, 'playlist-initial-static-pose')
    }
    else {
      initialAction.reset()
      initialAction.setEffectiveWeight(1)
      initialAction.setEffectiveTimeScale(1)
      initialAction.setLoop(THREE.LoopRepeat, Infinity) // idle动画循环播放
      initialAction.clampWhenFinished = false
      initialAction.play()
    }
  }

  playlistActions = actions
  playlistIndex = initialIndex
  const scheduleNextPlaylistSwitch = () => {
    const nextDelayMs = getRandomPlaylistDelayMs()
    playlistTimerId = setTimeout(() => {
      scheduleNextPlaylistSwitch()

      const currentAction = playlistActions[playlistIndex]
      // 随机选择下一个动画（避免连续选择同一个）
      let nextIndex = Math.floor(Math.random() * playlistActions.length)
      if (nextIndex === playlistIndex && playlistActions.length > 1) {
        nextIndex = (nextIndex + 1) % playlistActions.length
      }
      playlistIndex = nextIndex

      const nextAction = playlistActions[playlistIndex]

      if (isStaticHoldPoseAction(nextAction)) {
        startStaticPoseBlend(currentAction, nextAction, 'playlist-next-static-pose')
      }
      else {
        // Reset and prepare next action for cross-fade
        nextAction.reset()
        nextAction.setEffectiveWeight(0)
        nextAction.setEffectiveTimeScale(1)
        nextAction.setLoop(THREE.LoopRepeat, Infinity)
        nextAction.clampWhenFinished = false
        nextAction.play()

        // Cross-fade transition
        currentAction.crossFadeTo(nextAction, PLAYLIST_CROSSFADE_DURATION, false)
      }
    }, nextDelayMs)
  }

  // 定时切换到下一个随机idle动画
  scheduleNextPlaylistSwitch()
}

function stopPlaylistCycle() {
  if (playlistTimerId !== undefined) {
    clearInterval(playlistTimerId)
    playlistTimerId = undefined
  }

  // 移除greet动画监听器
  const mixer = vrmAnimationMixer.value
  if (mixer && greetAction) {
    mixer.removeEventListener('finished', onGreetAnimationFinished)
  }

  clearHoldPoseListener()
  clearStaticPoseBlend({ stopSourceAction: true })
  playlistActions = []
  playlistIndex = 0
  greetAction = undefined
}

// material type with optional update function for per-frame update, used for three-vrm's MToon material and custom shader materials with IBL injection
type UpdatableMaterial = Material & {
  update?: (delta: number) => void
}

// Expressions
const blink = useBlink()
const idleEyeSaccades = useIdleEyeSaccades()
const vrmEmote = ref<ReturnType<typeof useVRMEmote>>()
const vrmLipSync = useVRMLipSync(currentAudioSource)

// For sky box update
const nprProgramVersion = ref(0)
// For MToon IBL
let airiIblProbe: ReturnType<typeof createIblProbeController> | null = null
const stageThreeRuntimeTraceContext = getStageThreeRuntimeTraceContext()

function measureFrameStep(enabled: boolean, fn: () => void) {
  if (!enabled) {
    fn()
    return 0
  }

  const startedAt = performance.now()
  fn()
  return performance.now() - startedAt
}

function getRendererInstance() {
  return renderer?.instance as WebGLRenderer | undefined
}

function toErrorMessage(error: unknown) {
  if (error instanceof Error)
    return error.message
  if (typeof error === 'string')
    return error

  try {
    return JSON.stringify(error)
  }
  catch {
    return String(error)
  }
}

function emitVrmLoadError(reason: VrmLifecycleReason, startedAt: number, error: unknown) {
  if (!isStageThreeRuntimeTraceEnabled())
    return

  stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmLoadErrorEvent, {
    durationMs: performance.now() - startedAt,
    errorMessage: toErrorMessage(error),
    modelSrc: modelSrc.value,
    reason,
    rendererMemory: createThreeRendererMemorySnapshot(getRendererInstance()),
    sceneSummary: createVrmSceneSummarySnapshot({ mixer: vrmAnimationMixer.value, vrm: vrm.value }),
    ts: performance.now(),
  })
}

function invalidatePendingLoads() {
  loadSequence += 1
  return loadSequence
}

function isLoadRequestCurrent(requestId: number) {
  return loadSequence === requestId
}

function disposeDetachedVrm(detachedVrm?: VRM, detachedGroup?: Group) {
  detachedGroup?.removeFromParent()

  if (detachedVrm)
    VRMUtils.deepDispose(detachedVrm.scene as unknown as Object3D)
}

function detachVrmGroup(detachedGroup?: Group) {
  detachedGroup?.removeFromParent()
}

function createManagedVrmInstance(instance: Omit<ManagedVrmInstance, 'modelSrc' | 'scopeKey'>): ManagedVrmInstance {
  return {
    modelSrc: modelSrc.value!,
    scopeKey: getManagedVrmScopeKey(),
    ...instance,
  }
}

function getManagedVrmScopeKey() {
  return typeof window !== 'undefined' ? window.location.href : 'unknown'
}

function getActiveManagedVrmInstance() {
  if (!modelSrc.value || !vrm.value || !vrmGroup.value || !vrmAnimationMixer.value || !vrmEmote.value)
    return undefined

  return createManagedVrmInstance({
    emote: vrmEmote.value,
    group: vrmGroup.value,
    idlePlaybackMode: currentIdlePlaybackMode,
    mixer: vrmAnimationMixer.value,
    playlistActions: playlistActions.length ? playlistActions : undefined,
    vrm: vrm.value,
  })
}

function clearActiveManagedVrmRefs() {
  vrmAnimationMixer.value = undefined
  vrmEmote.value = undefined
  vrm.value = undefined
  vrmGroup.value = undefined
}

function applyManagedVrmInstance(instance: ManagedVrmInstance) {
  vrm.value = instance.vrm
  vrmGroup.value = instance.group
  vrmAnimationMixer.value = instance.mixer
  vrmEmote.value = instance.emote
  // Apply current rotation to the newly loaded model
  if (vrmGroup.value)
    vrmGroup.value.rotation.y = MathUtils.degToRad(modelRotationY.value)
}

function destroyManagedVrmInstance(instance?: ManagedVrmInstance) {
  if (!instance)
    return

  instance.emote.dispose()
  instance.mixer.stopAllAction()
  disposeDetachedVrm(instance.vrm, instance.group)
}

function isManagedVrmInstanceReusable(instance: ManagedVrmInstance) {
  try {
    instance.group.updateMatrixWorld(true)
    instance.vrm.scene.updateMatrixWorld(true)
    instance.vrm.humanoid.update()
    return true
  }
  catch {
    return false
  }
}

function shouldDestroyVrmResources(reason: VrmLifecycleReason) {
  return reason === 'model-switch'
}

function shouldStashVrmResources(reason: VrmLifecycleReason) {
  return reason === 'component-unmount'
}

function updateManagedVrmMaterials(activeVrm: VRM | undefined, delta: number) {
  // NOTICE: three-vrm drives MToon per-frame uniforms, including alphaTest used by MASK cutout,
  // through material.update(delta). Our render loop updates VRM subsystems manually instead of
  // calling vrm.update(delta), so material updates must be forwarded here as well.
  activeVrm?.materials?.forEach((material) => {
    (material as UpdatableMaterial).update?.(delta)
  })
}

function runVrmLoadHooks(context: VrmLoadHookContext) {
  for (const hook of vrmHooks) {
    hook.onLoad?.(context)
  }
}

function runVrmMaterialHooks(context: VrmMaterialHookContext) {
  for (const hook of vrmHooks) {
    hook.onMaterial?.(context)
  }
}

function runVrmFrameHooks(context: VrmFrameHookContext) {
  for (const hook of vrmHooks) {
    try {
      hook.onFrame?.(context)
    }
    catch (error) {
      console.error(error)
      emit('error', error)
    }
  }
}

function runVrmFrameRuntimeHook(vrm: VRM, delta: number) {
  try {
    vrmFrameRuntimeHook.value?.(vrm, delta)
  }
  catch (error) {
    console.error(error)
    emit('error', error)
  }
}

function getOrCreateIdleNoiseMotionState(activeVrm: VRM): IdleNoiseMotionState {
  const existing = idleNoiseMotionStateByVrm.get(activeVrm)
  if (existing) {
    return existing
  }

  const springJoints = Array.from(activeVrm.springBoneManager?.joints ?? []).map(joint => ({
    joint,
    originalDragForce: joint.settings.dragForce,
    originalGravityDir: joint.settings.gravityDir.clone(),
    originalGravityPower: joint.settings.gravityPower,
    originalStiffness: joint.settings.stiffness,
  }))

  const created: IdleNoiseMotionState = {
    elapsedSeconds: Math.random() * 10,
    springJoints,
  }
  idleNoiseMotionStateByVrm.set(activeVrm, created)
  return created
}

function restoreIdleNoiseMotionState(activeVrm: VRM | undefined) {
  if (!activeVrm) {
    return
  }

  const state = idleNoiseMotionStateByVrm.get(activeVrm)
  if (!state) {
    return
  }

  for (const springState of state.springJoints) {
    springState.joint.settings.dragForce = springState.originalDragForce
    springState.joint.settings.gravityDir.copy(springState.originalGravityDir)
    springState.joint.settings.gravityPower = springState.originalGravityPower
    springState.joint.settings.stiffness = springState.originalStiffness
  }
}

function applyIdleNoiseMotion(activeVrm: VRM, delta: number) {
  const safeDelta = Math.min(Math.max(delta, 0), MAX_SAFE_SPRING_BONE_DELTA_SECONDS)
  const state = getOrCreateIdleNoiseMotionState(activeVrm)
  state.elapsedSeconds += safeDelta

  const t = state.elapsedSeconds
  const hips = activeVrm.humanoid?.getRawBoneNode('hips')
  if (hips) {
    hips.rotation.z += Math.sin(t * 0.55) * IDLE_NOISE_HIPS_SWAY_RAD
      + Math.sin(t * 1.21 + 0.8) * IDLE_NOISE_HIPS_SWAY_RAD * 0.35
    hips.rotation.y += Math.sin(t * 0.33 + 0.6) * IDLE_NOISE_HIPS_TWIST_RAD
  }

  const headPitch = Math.sin(t * 0.47 + 0.9) * IDLE_NOISE_HEAD_PITCH_RAD
    + Math.sin(t * 1.14 + 1.7) * IDLE_NOISE_HEAD_PITCH_RAD * 0.25
  const headYaw = Math.sin(t * 0.36 + 1.4) * IDLE_NOISE_HEAD_YAW_RAD
  const headRoll = Math.sin(t * 0.62 + 0.2) * IDLE_NOISE_HEAD_ROLL_RAD
    + Math.sin(t * 1.31 + 2.1) * IDLE_NOISE_HEAD_ROLL_RAD * 0.2
  const neck = activeVrm.humanoid?.getRawBoneNode('neck')
  const head = activeVrm.humanoid?.getRawBoneNode('head')

  if (neck) {
    neck.rotation.x += headPitch * IDLE_NOISE_NECK_FOLLOW_RATIO
    neck.rotation.y += headYaw * IDLE_NOISE_NECK_FOLLOW_RATIO
    neck.rotation.z += headRoll * IDLE_NOISE_NECK_FOLLOW_RATIO
  }
  if (head) {
    head.rotation.x += headPitch
    head.rotation.y += headYaw
    head.rotation.z += headRoll
  }

  for (const [index, springState] of state.springJoints.entries()) {
    if (springState.originalGravityPower <= IDLE_NOISE_ZERO_GRAVITY_EPSILON) {
      springState.joint.settings.dragForce = springState.originalDragForce
      springState.joint.settings.gravityDir.copy(springState.originalGravityDir)
      springState.joint.settings.gravityPower = springState.originalGravityPower
      springState.joint.settings.stiffness = springState.originalStiffness
      continue
    }

    // Derive a low-frequency directional breeze from the authored gravity setup,
    // so hair / cloth keep their original character while reacting to ambient wind.
    const gust = Math.sin(t * 0.27 + index * 0.73) * 0.6
      + Math.sin(t * 0.91 + index * 1.37) * 0.4
    const gustMagnitude = Math.abs(gust)
    idleNoiseWindVector.set(
      gust * 0.18 * IDLE_NOISE_INTENSITY_SCALE,
      0,
      Math.sin(t * 0.43 + index * 0.53) * 0.08 * IDLE_NOISE_INTENSITY_SCALE,
    )
    idleNoiseGravityVector.copy(springState.originalGravityDir).add(idleNoiseWindVector).normalize()

    springState.joint.settings.gravityDir.copy(idleNoiseGravityVector)
    springState.joint.settings.gravityPower = springState.originalGravityPower * (1 + gustMagnitude * 0.18 * IDLE_NOISE_INTENSITY_SCALE)
    springState.joint.settings.dragForce = MathUtils.clamp(
      springState.originalDragForce + 0.02 * IDLE_NOISE_INTENSITY_SCALE + gustMagnitude * 0.05 * IDLE_NOISE_INTENSITY_SCALE,
      0,
      0.98,
    )
    springState.joint.settings.stiffness = Math.max(
      springState.originalStiffness * (1 - gustMagnitude * 0.08 * IDLE_NOISE_INTENSITY_SCALE),
      0,
    )
  }

  activeVrm.scene.updateMatrixWorld(true)
}

function runVrmDisposeHooks(context: VrmDisposeHookContext) {
  for (const hook of vrmHooks) {
    try {
      hook.onDispose?.(context)
    }
    catch (error) {
      console.error(error)
      emit('error', error)
    }
  }
}

function runVrmDisposeHooksForInstance(instance: ManagedVrmInstance | undefined, reason: VrmLifecycleReason) {
  if (!instance)
    return

  runVrmDisposeHooks({
    camera: camera.value,
    reason,
    vrm: instance.vrm,
    vrmGroup: instance.group,
  })
}

function destroyManagedVrmInstanceWithHooks(instance: ManagedVrmInstance | undefined, reason: VrmLifecycleReason) {
  if (!instance)
    return

  runVrmDisposeHooksForInstance(instance, reason)
  destroyManagedVrmInstance(instance)
}

function bindManagedVrmInstanceRenderLoop() {
  disposeBeforeRenderLoop?.()

  disposeBeforeRenderLoop = onBeforeRender(({ delta }) => {
    // Manually update VRM components in the render loop because we manage the render loop on our own.
    // See:
    // 1. https://github.com/pixiv/three-vrm/blob/2c4aac612467216e0c8e7dc4500c2fa309208cc7/packages/three-vrm-core/src/VRMCore.ts#L72-L82
    // 2. https://github.com/pixiv/three-vrm/blob/2c4aac612467216e0c8e7dc4500c2fa309208cc7/packages/three-vrm/src/VRM.ts#L49-L67
    const traceStart = isStageThreeRuntimeTraceEnabled() ? performance.now() : 0
    const tracingEnabled = traceStart > 0

    const animationMixerMs = measureFrameStep(tracingEnabled, () => {
      updateStaticPoseBlend(delta)
      vrmAnimationMixer.value?.update(delta)
    })
    const activeVrm = vrm.value
    const activeVrmGroup = vrmGroup.value
    updateManagedVrmMaterials(activeVrm, delta)
    const vrmFrameHookMs = measureFrameStep(tracingEnabled, () => {
      if (activeVrm && activeVrmGroup) {
        runVrmFrameHooks({
          camera: camera.value,
          delta,
          vrm: activeVrm,
          vrmGroup: activeVrmGroup,
        })
      }
    })
    const vrmRuntimeHookMs = measureFrameStep(tracingEnabled, () => {
      if (activeVrm)
        runVrmFrameRuntimeHook(activeVrm, delta)
    })
    const humanoidMs = measureFrameStep(tracingEnabled, () => {
      activeVrm?.humanoid.update()
    })
    const lookAtMs = measureFrameStep(tracingEnabled, () => {
      activeVrm?.lookAt?.update?.(delta)
    })
    const blinkAndSaccadeMs = measureFrameStep(tracingEnabled, () => {
      blink.update(activeVrm, delta)
    })
    const emoteMs = measureFrameStep(tracingEnabled, () => {
      vrmEmote.value?.update(delta)
    })
    const lipSyncMs = measureFrameStep(tracingEnabled, () => {
      vrmLipSync.update(activeVrm, delta)
    })
    const expressionMs = measureFrameStep(tracingEnabled, () => {
      activeVrm?.expressionManager?.update()
    })
    const nodeConstraintMs = measureFrameStep(tracingEnabled, () => {
      activeVrm?.nodeConstraintManager?.update()
    })
    const springBoneMs = measureFrameStep(tracingEnabled, () => {
      if (!activeVrm)
        return

      applyIdleNoiseMotion(activeVrm, delta)
      activeVrm.springBoneManager?.update(Math.min(delta, MAX_SAFE_SPRING_BONE_DELTA_SECONDS))
    })

    if (traceStart > 0) {
      stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmUpdateFrameEvent, {
        animationMixerMs,
        blinkAndSaccadeMs,
        deltaMs: delta * 1000,
        durationMs: performance.now() - traceStart,
        emoteMs,
        expressionMs,
        humanoidMs,
        lipSyncMs,
        lookAtMs,
        nodeConstraintMs,
        springBoneMs,
        ts: traceStart,
        vrmFrameHookMs,
        vrmRuntimeHookMs,
      })
    }
  }).off
}

function commitManagedVrmInstance(instance: ManagedVrmInstance) {
  scene.value?.add(instance.group)
  applyManagedVrmInstance(instance)
  bindManagedVrmInstanceRenderLoop()

  // 根据nextAnimationPhase启动动画播放
  if (instance.playlistActions?.length) {
    const actions = instance.playlistActions
    const greetAction = nextAnimationPhase === 'greet' ? actions[0] : undefined
    const idleActions = greetAction ? actions.slice(1) : actions
    const idlePlaybackMode = instance.idlePlaybackMode ?? 'cycle'

    if (nextAnimationPhase === 'greet' && greetAction && idleActions.length > 0) {
      playGreetAnimation(greetAction, idleActions, idlePlaybackMode)
    }
    else if (idleActions.length > 0) {
      if (idlePlaybackMode === 'hold') {
        playHoldPoseAction(idleActions[0], { reuseExisting: true })
        playlistActions = [idleActions[0]]
        playlistIndex = 0
        currentIdlePlaybackMode = 'hold'
      }
      else {
        startPlaylistCycle(idleActions)
      }
    }
  }

  emit('loaded', modelSrc.value!)
  modelLoaded.value = true
}

// clean the previous vrm model loaded
function componentCleanUp(
  reason: VrmLifecycleReason,
  options: { invalidate?: boolean } = {},
) {
  const { invalidate = true } = options
  if (invalidate)
    invalidatePendingLoads()

  const startedAt = performance.now()
  const activeInstance = getActiveManagedVrmInstance()
  const shouldDestroyResources = shouldDestroyVrmResources(reason)
  const clearedInstance = shouldDestroyResources ? clearManagedVrmInstance(getManagedVrmScopeKey()) : undefined
  const rendererInstance = getRendererInstance()
  const hasCleanupWork = !!disposeBeforeRenderLoop
    || !!activeInstance
    || !!airiIblProbe

  if (hasCleanupWork && isStageThreeRuntimeTraceEnabled()) {
    stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmDisposeStartEvent, {
      modelSrc: modelSrc.value,
      reason,
      rendererMemory: createThreeRendererMemorySnapshot(rendererInstance),
      sceneSummary: createVrmSceneSummarySnapshot({ mixer: activeInstance?.mixer, vrm: activeInstance?.vrm }),
      ts: startedAt,
    })
  }

  disposeBeforeRenderLoop?.()
  disposeBeforeRenderLoop = undefined

  // Stop the idle playlist cycler; it will be restarted if the instance is
  // re-committed (e.g. cache take on remount).
  stopPlaylistCycle()
  restoreIdleNoiseMotionState(activeInstance?.vrm)
  restoreIdleNoiseMotionState(clearedInstance?.vrm)

  if (activeInstance)
    detachVrmGroup(activeInstance.group)

  if (shouldDestroyResources) {
    destroyManagedVrmInstanceWithHooks(activeInstance, reason)
    destroyManagedVrmInstanceWithHooks(clearedInstance, reason)
  }
  else if (shouldStashVrmResources(reason)) {
    destroyManagedVrmInstanceWithHooks(activeInstance ? stashManagedVrmInstance(activeInstance) : undefined, reason)
  }
  else {
    destroyManagedVrmInstanceWithHooks(activeInstance, reason)
  }

  airiIblProbe?.dispose()
  airiIblProbe = null
  clearActiveManagedVrmRefs()
  modelLoaded.value = false

  if (hasCleanupWork && isStageThreeRuntimeTraceEnabled()) {
    stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmDisposeEndEvent, {
      durationMs: performance.now() - startedAt,
      modelSrc: modelSrc.value,
      reason,
      rendererMemory: createThreeRendererMemorySnapshot(rendererInstance),
      sceneSummary: createVrmSceneSummarySnapshot(),
      ts: performance.now(),
    })
  }
}

const defaultTookAt = computed(() => new Vector3(0, eyeHeight.value, -100))

function computeBoundingBox(vrmScene: Object3D) {
  const box = new Box3()
  const childBox = new Box3()

  vrmScene.updateMatrixWorld(true)

  vrmScene.traverse((obj) => {
    if (!obj.visible)
      return

    const mesh = obj as Mesh
    if (!mesh.isMesh || !mesh.geometry)
      return

    if (mesh.name.startsWith('VRMC_springBone_collider'))
      return

    if (!mesh.geometry.boundingBox)
      mesh.geometry.computeBoundingBox()

    childBox.copy(mesh.geometry.boundingBox!)
    childBox.applyMatrix4(mesh.matrixWorld)
    box.union(childBox)
  })

  return box
}

function getEyePosition(activeVrm: VRM): number | null {
  const eye = activeVrm.humanoid?.getNormalizedBoneNode('head')
  if (!eye)
    return null

  const eyePos = new Vector3()
  eye.getWorldPosition(eyePos)
  return eyePos.y
}

function buildSceneBootstrap(activeVrm: VRM, cacheHit: boolean): SceneBootstrap {
  const bootstrapRoot = activeVrm.scene.parent ?? activeVrm.scene
  const box = computeBoundingBox(bootstrapRoot)
  const modelSize = new Vector3()
  const modelCenter = new Vector3()
  box.getSize(modelSize)
  box.getCenter(modelCenter)

  const fov = camera.value?.fov ?? 40
  const radians = (fov / 2 * Math.PI) / 180
  const eyePositionY = getEyePosition(activeVrm) ?? modelCenter.y

  // Portrait (mobile) framing: the desktop offset frames ~2/3 of the body and
  // biases the orbit target upward, which on a tall narrow canvas leaves a
  // large blank gap between the model and the chat input. On portrait viewports
  // keep the true body center as the orbit target and pull the camera back so
  // the full body fits with a small margin.
  // NOTICE: neither camera.aspect (constructor default 1) nor the renderer
  // canvas (may still be the 300x150 default while Screen hasn't applied its
  // computed size — cached models can load faster than that) is reliable at
  // bootstrap time. The window viewport is race-free.
  const isPortrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth

  if (!isPortrait)
    modelCenter.y += modelSize.y / 5

  const initialCameraOffset = isPortrait
    ? new Vector3(
        0,
        0,
        -((modelSize.y / 2) * 1.15) / Math.tan(radians),
      )
    : new Vector3(
        modelSize.x / 16,
        modelSize.y / 8,
        -(modelSize.y / 3) / Math.tan(radians),
      )

  const cameraPosition = modelCenter.clone().add(initialCameraOffset)
  const lookAtTarget = isPortrait
    ? modelCenter.clone()
    : defaultTookAt.value

  return {
    cacheHit,
    cameraDistance: cameraPosition.distanceTo(modelCenter),
    cameraPosition: { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z },
    eyeHeight: eyePositionY,
    lookAtTarget,
    modelOffset: {
      x: bootstrapRoot.position.x,
      y: bootstrapRoot.position.y,
      z: bootstrapRoot.position.z,
    },
    modelOrigin: { x: modelCenter.x, y: modelCenter.y, z: modelCenter.z },
    modelSize: { x: modelSize.x, y: modelSize.y, z: modelSize.z },
  }
}

function resolveVrmLoadReason(): 'initial-load' | 'model-reload' | 'model-switch' {
  if (!lastCommittedModelSrc.value)
    return 'initial-load'

  if (lastCommittedModelSrc.value !== modelSrc.value)
    return 'model-switch'

  return 'model-reload'
}

async function loadModel() {
  const requestId = invalidatePendingLoads()
  const currentLoadReason = resolveVrmLoadReason()
  const loadStartedAt = performance.now()
  let nextVrm: VRM | undefined
  let nextVrmGroup: Group | undefined
  let nextVrmAnimationMixer: AnimationMixer | undefined
  let nextVrmEmote: ReturnType<typeof useVRMEmote> | undefined
  let nextPlaylistActions: AnimationAction[] | undefined
  let didCommitLoad = false

  try {
    if (!scene.value) {
      await until(() => scene.value).toBeTruthy()
      if (!isLoadRequestCurrent(requestId))
        return
    }
    if (!modelSrc.value) {
      console.warn('NO model src, cannot load VRM model.')
      return
    }

    emit('loadStart', currentLoadReason)

    if (isStageThreeRuntimeTraceEnabled()) {
      stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmLoadStartEvent, {
        modelSrc: modelSrc.value,
        reason: currentLoadReason,
        rendererMemory: createThreeRendererMemorySnapshot(getRendererInstance()),
        sceneSummary: createVrmSceneSummarySnapshot(),
        ts: loadStartedAt,
      })
    }

    modelLoaded.value = false

    const reusableInstance = takeManagedVrmInstance(getManagedVrmScopeKey(), modelSrc.value)
    if (reusableInstance) {
      if (!isManagedVrmInstanceReusable(reusableInstance)) {
        destroyManagedVrmInstanceWithHooks(reusableInstance, currentLoadReason)
      }
      else {
        if (!isLoadRequestCurrent(requestId)) {
          destroyManagedVrmInstanceWithHooks(stashManagedVrmInstance(reusableInstance), currentLoadReason)
          return
        }

        nextVrm = reusableInstance.vrm
        nextVrmGroup = reusableInstance.group
        nextVrmAnimationMixer = reusableInstance.mixer
        nextVrmEmote = reusableInstance.emote

        if (!airiIblProbe && scene.value)
          airiIblProbe = createIblProbeController(scene.value)

        if (currentLoadReason === 'model-switch') {
          componentCleanUp('model-switch', { invalidate: false })
        }

        runVrmLoadHooks({
          cacheHit: true,
          camera: camera.value,
          reason: currentLoadReason,
          vrm: reusableInstance.vrm,
          vrmGroup: reusableInstance.group,
        })
        emit('sceneBootstrap', buildSceneBootstrap(reusableInstance.vrm, true))
        commitManagedVrmInstance(reusableInstance)
        didCommitLoad = true

        if (isStageThreeRuntimeTraceEnabled()) {
          stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmLoadEndEvent, {
            durationMs: performance.now() - loadStartedAt,
            modelSrc: modelSrc.value,
            reason: currentLoadReason,
            rendererMemory: createThreeRendererMemorySnapshot(getRendererInstance()),
            sceneSummary: createVrmSceneSummarySnapshot({ mixer: reusableInstance.mixer, vrm: reusableInstance.vrm }),
            ts: performance.now(),
          })
        }
        return
      }
    }

    // Check binary data cache first (avoids repeated network requests on model switch)
    let actualModelUrl = modelSrc.value
    const cachedData = getVrmDataCache(modelSrc.value)
    if (cachedData) {
      const blob = new Blob([cachedData], { type: 'model/gltf-binary' })
      actualModelUrl = URL.createObjectURL(blob)
    }
    else {
      // 🗜️ 压缩传输：如果服务器返回 gzip，前端解压后再加载
      const isGzipUrl = modelSrc.value.includes('.glb.gz') || modelSrc.value.includes('model.vrm')
      if (isGzipUrl) {
        try {
          const resp = await fetch(modelSrc.value)
          const compressedData = await resp.arrayBuffer()

          if (!resp.ok) {
            emit('error', new Error(`Failed to fetch model: ${resp.status} ${resp.statusText}`))
            return
          }

          // 检查是否是压缩数据（通过 header 或 magic number）
          const isCompressed = resp.headers.get('X-Model-Compressed') === 'true'
            || (compressedData.byteLength > 2 && new Uint8Array(compressedData, 0, 2)[0] === 0x1F && new Uint8Array(compressedData, 0, 2)[1] === 0x8B)

          if (isCompressed) {
            const ds = new DecompressionStream('gzip')
            const writer = ds.writable.getWriter()
            writer.write(new Uint8Array(compressedData))
            writer.close()

            const decompressedData = await new Response(ds.readable).arrayBuffer()
            setVrmDataCache(modelSrc.value, decompressedData)
            const blob = new Blob([decompressedData], { type: 'model/gltf-binary' })
            actualModelUrl = URL.createObjectURL(blob)
          }
          else {
            setVrmDataCache(modelSrc.value, compressedData)
          }
        }
        catch (e) {
          // decompress failed, fallback to original URL
        }
      }
    }

    const _vrmInfo = await loadVrm(actualModelUrl, {
      lookAt: true,
      onProgress: (progress) => {
        const currentProgress = Number((100 * progress.loaded / progress.total).toFixed(2))

        if (currentProgress === 100) {
          // download complete
        }

        emit('loadingProgress', currentProgress)
      },
    })
    if (!_vrmInfo || !_vrmInfo._vrm || !_vrmInfo._vrmGroup) {
      if (isLoadRequestCurrent(requestId)) {
        emitVrmLoadError(currentLoadReason, loadStartedAt, 'VRM model loading failure')
        emit('error', new Error('VRM model loading failure'))
      }
      return
    }
    const {
      _vrm,
      _vrmGroup,
    } = _vrmInfo
    nextVrm = _vrm
    nextVrmGroup = _vrmGroup

    if (!isLoadRequestCurrent(requestId)) {
      disposeDetachedVrm(nextVrm, nextVrmGroup)
      return
    }

    runVrmLoadHooks({
      cacheHit: false,
      camera: camera.value,
      reason: currentLoadReason,
      vrm: _vrm,
      vrmGroup: _vrmGroup,
    })

    /*
      * Animation setting
    */
    let greetAnimationUrl: string | undefined
    let idleAnimationUrls: string[] = []
    let effectiveAnimationPhase: 'greet' | 'idle' = animationPhase.value || 'idle'

    if (effectiveAnimationPhase === 'greet' && greetAnimation.value) {
      greetAnimationUrl = greetAnimation.value
      idleAnimationUrls = idleAnimationPlaylist.value || []
    }
    else {
      effectiveAnimationPhase = 'idle'
      idleAnimationUrls = idleAnimationPlaylist.value?.length
        ? idleAnimationPlaylist.value
        : idleAnimation.value ? [idleAnimation.value] : []
    }

    const animationUrlsToLoad = greetAnimationUrl
      ? [greetAnimationUrl, ...idleAnimationUrls]
      : idleAnimationUrls

    const greetAnimationIndex = greetAnimationUrl ? 0 : -1

    // 只有在有动画URL时才加载动画
    if (animationUrlsToLoad.length > 0) {
      const settledAnimations = await Promise.allSettled(
        animationUrlsToLoad.map(url => loadVRMAnimation(url)),
      )
      if (!isLoadRequestCurrent(requestId)) {
        disposeDetachedVrm(nextVrm, nextVrmGroup)
        return
      }

      // Build clips for every animation that loaded successfully.
      const clips: AnimationClip[] = []
      for (const settled of settledAnimations) {
        if (settled.status !== 'fulfilled' || !settled.value)
          continue
        const clip = await clipFromVRMAnimation(_vrm, settled.value)
        if (!clip)
          continue
        reAnchorRootPositionTrack(clip, _vrm)
        clips.push(clip)
      }

      // 动画加载失败不影响模型显示，只是没有动画而已
      if (clips.length > 0) {
        nextVrmAnimationMixer = new AnimationMixer(_vrm.scene)
        const actions = clips.map(clip => nextVrmAnimationMixer!.clipAction(clip))

        const greetAction = greetAnimationIndex >= 0 ? actions[greetAnimationIndex] : undefined
        const idleActions = greetAnimationIndex >= 0
          ? actions.slice(greetAnimationIndex + 1)
          : actions
        nextIdlePlaybackMode = idleActions.length > 1 ? 'cycle' : 'hold'

        if (effectiveAnimationPhase === 'greet' && greetAction && idleActions.length > 0) {
          nextPlaylistActions = actions
        }
        else if (idleActions.length > 0) {
          nextPlaylistActions = idleActions
          if (nextIdlePlaybackMode === 'hold' && effectiveAnimationPhase === 'idle') {
            const holdAction = idleActions[0]
            playHoldPoseAction(holdAction)
          }
        }
        else {
          nextPlaylistActions = actions
        }
      }
    }

    // 确保mixer总是有值（即使没有动画）
    if (!nextVrmAnimationMixer) {
      nextVrmAnimationMixer = new AnimationMixer(_vrm.scene)
    }

    // 保存effectiveAnimationPhase供commitManagedVrmInstance使用
    nextAnimationPhase = effectiveAnimationPhase

    nextVrmEmote = useVRMEmote(_vrm)

    /*
      * Shader setting
    */
    const isShaderMat = (m: any): m is ShaderMaterial => !!m?.isShaderMaterial

    function configureInjectedShaderMaterial(mat: ShaderMaterial) {
      if ('toneMapped' in mat)
        mat.toneMapped = false
      if ('envMap' in mat && mat.envMap)
        mat.envMap = null

      // NPR materials usually use sRGB textures.
      const tex = (mat as any).map as Texture | undefined
      if (tex && (tex as any).colorSpace !== undefined) {
        try {
          (tex as any).colorSpace = SRGBColorSpace
        }
        catch (e) {
          console.warn('Failed to set colorSpace on texture:', e)
        }
      }

      injectDiffuseIBL(mat)
    }

    // MToon material sky box lightProbe setting
    if (!airiIblProbe && scene.value)
      airiIblProbe = createIblProbeController(scene.value)

    // Material traverse setting
    _vrm.scene.traverse((child) => {
      if (child instanceof Mesh && child.material) {
        const material = Array.isArray(child.material) ? child.material : [child.material]
        material.forEach((mat, materialIndex) => {
          if (mat instanceof MeshStandardMaterial || mat instanceof MeshPhysicalMaterial) {
            // Should read envMap intensity from outside props
            mat.envMapIntensity = 1.0
            mat.needsUpdate = true
          }
          else if (mat?.isMToonMaterial) {
            // --- MToon material ---
            // NOTICE: three-vrm MToon already consumes scene LightProbe irradiance.
            // Keep it on a single IBL path to avoid double-applying diffuse IBL.
            if ('toneMapped' in mat)
              mat.toneMapped = false

            // NOTICE:
            // Workaround for VRM MToon shade color being too dark or pure dark red in the back.
            // MToon ignores secondary directional lights for its main Lit/Shade step.
            // We manually blend the shade color with the lit color to make the shadows look more natural.
            if (mat.shadeColorFactor && mat.color) {
              mat.shadeColorFactor.lerp(mat.color, 0.55)
            }
          }
          else if (isShaderMat(mat)) {
            // --- Shader material, further IBL injection needed ---
            // TODO: stylised shader injection
            // Lilia: I plan to replace all injected shader code to be my own, so that it can always avoid double injection and unknown user upload VRM injected shader behaviour...
            configureInjectedShaderMaterial(mat)
          }

          runVrmMaterialHooks({
            camera: camera.value,
            material: mat,
            materialIndex,
            mesh: child,
            reason: currentLoadReason,
            vrm: _vrm,
            vrmGroup: _vrmGroup,
          })
        })
      }
    })

    if (currentLoadReason === 'model-switch') {
      componentCleanUp('model-switch', { invalidate: false })
    }

    emit('sceneBootstrap', buildSceneBootstrap(_vrm, false))

    commitManagedVrmInstance(createManagedVrmInstance({
      emote: nextVrmEmote,
      group: _vrmGroup,
      idlePlaybackMode: nextIdlePlaybackMode,
      mixer: nextVrmAnimationMixer,
      playlistActions: nextPlaylistActions,
      vrm: _vrm,
    }))
    didCommitLoad = true

    if (isStageThreeRuntimeTraceEnabled()) {
      stageThreeRuntimeTraceContext.emit(stageThreeTraceVrmLoadEndEvent, {
        durationMs: performance.now() - loadStartedAt,
        modelSrc: modelSrc.value,
        reason: currentLoadReason,
        rendererMemory: createThreeRendererMemorySnapshot(getRendererInstance()),
        sceneSummary: createVrmSceneSummarySnapshot({ mixer: vrmAnimationMixer.value, vrm: _vrm }),
        ts: performance.now(),
      })
    }
  }
  catch (err) {
    if (!didCommitLoad) {
      if (nextVrm && nextVrmGroup) {
        runVrmDisposeHooks({
          camera: camera.value,
          reason: currentLoadReason,
          vrm: nextVrm,
          vrmGroup: nextVrmGroup,
        })
      }

      nextVrmEmote?.dispose()
      nextVrmAnimationMixer?.stopAllAction()
      disposeDetachedVrm(nextVrm, nextVrmGroup)
    }
    if (!isLoadRequestCurrent(requestId))
      return

    emitVrmLoadError(currentLoadReason, loadStartedAt, err)
    console.error(err)
    emit('error', err)
  }
}

const focusPos = useVRMEyeFocusFor({
  cameraPosition: () => props.cameraPosition,
  context: () => ({
    camera: camera.value,
    raycaster,
    defaultLookAt: defaultTookAt.value,
  }),
  screenBoundingBox: props.screenBoundingBox,
  source: () => props.cursorPosition,
  trackingMode: () => props.trackingMode,
})

onMounted(async () => {
  // watch if the model needs to be reloaded
  // Registered BEFORE the initial load to avoid missing src changes
  // that arrive while the first loadModel() is still in-flight.
  watch(modelSrc, (newSrc, oldSrc) => {
    if (newSrc !== oldSrc) {
      loadModel()
    }
  })

  // wait until scene is not undefined
  await until(() => scene.value).toBeTruthy()
  await loadModel()

  /*
    * Downward info flow
    * - Pinia store value updated => command take effect
  */
  // watch if the animation should be paused
  watch(paused, (isPaused) => {
    if (isPaused) {
      stop()
    }
    else {
      start()
    }
  }, { immediate: true })
  // update model position
  watch(modelOffset, () => {
    if (vrmGroup.value) {
      vrmGroup.value.position.set(
        modelOffset.value.x,
        modelOffset.value.y,
        modelOffset.value.z,
      )
    }
  }, { immediate: true, deep: true })
  // update model rotation
  watch(modelRotationY, (newRotationY) => {
    if (vrmGroup.value) {
      vrmGroup.value.rotation.y = MathUtils.degToRad(newRotationY)
    }
  }, { immediate: true })
  // update NPR sky box
  watch([envSelect, skyBoxIntensity, nprIrrSH], async () => {
    if (!vrm.value)
      return
    // force the program to flush
    nprProgramVersion.value += 1
    const mode = normalizeEnvMode(envSelect.value)

    // TODO: after bumping up to three 0.180.0 with @types/three 0.180.0,
    //   Argument of type 'Group<Object3DEventMap>' is not assignable to parameter of type 'Object3D<Object3DEventMap>'.
    //     Type 'Group<Object3DEventMap>' is missing the following properties from type 'Object3D<Object3DEventMap>': setPointerCapture, releasePointerCapture, hasPointerCapture
    //
    // Currently, AFAIK, https://github.com/pmndrs/xr/blob/456aa380206e93888cd3a5741a1534e672ae3106/packages/pointer-events/src/pointer.ts#L69-L100 declares
    // declare module 'three' {
    //   interface Object3D {
    //     setPointerCapture(pointerId: number): void
    //     releasePointerCapture(pointerId: number): void
    //     hasPointerCapture(pointerId: number): boolean

    //     intersectChildren?: boolean
    //     interactableDescendants?: Array<Object3D>
    //     /**
    //      * @deprecated
    //      */
    //     ancestorsHaveListeners?: boolean
    //     ancestorsHavePointerListeners?: boolean
    //     ancestorsHaveWheelListeners?: boolean
    //   }
    // }
    //
    // And in @tresjs/core v5, it uses the @pmndrs/pointer-events internally.
    // Somehow the Object3D from @types/three and the one augmented by @pmndrs/pointer-events are not compatible.
    // This needs to be fixed later.
    updateNprShaderSetting(vrm.value?.scene as unknown as Object3D, {
      mode,
      intensity: skyBoxIntensity.value,
      sh: nprIrrSH.value ?? null,
    })
    airiIblProbe?.update(mode, skyBoxIntensity.value, nprIrrSH.value ?? null)
  }, { immediate: true })
  watch(focusPos, (newPos) => {
    idleEyeSaccades.instantUpdate(vrm.value, newPos)
  }, { immediate: true })
})

onUnmounted(() => {
  componentCleanUp('component-unmount')
})

if (import.meta.hot) {
  // Ensure cleanup on HMR
  import.meta.hot.dispose(() => {
    componentCleanUp('manual-reload')
  })
}

defineExpose({
  setExpression(expression: string, intensity = 1, durationMs = 1500) {
    vrmEmote.value?.setEmotionWithResetAfter(expression, durationMs, intensity)
  },
  // NOTICE: This runtime frame hook is intentionally separate from internal VRM model hooks.
  // External callers use it for live pose/tracking input; internal hooks remain reserved for
  // stage-ui-three's own model/material lifecycle extensions.
  setVrmFrameHook(hook?: VrmFrameRuntimeHook) {
    vrmFrameRuntimeHook.value = hook
  },
  scene: computed(() => vrm.value?.scene),
  lookAtUpdate(target: Vec3) {
    idleEyeSaccades.instantUpdate(vrm.value, target)
  },
})
</script>

<template>
  <slot v-if="modelLoaded" />
</template>
